import { MELEE } from "@eldrun/content";

/** O que move o braço: golpe corpo a corpo, golpe de coleta ou o disparo do arco. */
export type SwingKind = "golpe" | "coleta" | "arco";

/** Um golpe: começo e ciclo em ms. */
interface Swing {
  start: number;
  cycle: number;
  kind: SwingKind;
}

/** Golpes em andamento por corpo (o próprio, na hora; os dos outros, pelo evento do servidor). */
export class SwingTracker {
  private readonly swings = new Map<string, Swing>();

  start(id: string, now: number, cycleSeconds: number, kind: SwingKind = "golpe"): void {
    this.swings.set(id, { start: now, cycle: cycleSeconds * 1000, kind });
  }

  /** O corpo saiu: esquece os golpes dele. */
  forget(id: string): void {
    this.swings.delete(id);
  }

  /** O tipo do golpe em andamento (o progresso diz se ainda está). */
  kind(id: string): SwingKind | undefined {
    return this.swings.get(id)?.kind;
  }

  /**
   * Progresso do golpe de 0 a 1 (0 = sem golpe). Um golpe dos outros começa na hora da mensagem
   * (`performance.now()`), e o quadro é lido com o instante do começo dele (o do `requestAnimationFrame`),
   * que pode ser um pouco anterior: antes do início conta como recém-começado, não como acabado.
   */
  progress(id: string, now: number): number {
    const swing = this.swings.get(id);
    if (!swing) return 0;
    const p = (now - swing.start) / swing.cycle;
    if (p >= 1) {
      this.swings.delete(id);
      return 0;
    }
    return Math.max(p, 1e-3);
  }
}

/**
 * Ângulo do braço direito durante o golpe: ergue até o impacto (35 % do ciclo) e desce com
 * força; depois volta. Radianos em torno de x (negativo = para cima/trás).
 */
export function swingArm(progress: number): number {
  const impact = MELEE.impactFraction;
  if (progress <= 0) return 0;
  if (progress < impact) return -2.4 * (progress / impact);
  const t = (progress - impact) / (1 - impact);
  // Desce rápido até ~0,9 rad à frente e relaxa.
  return t < 0.35 ? -2.4 + 3.3 * (t / 0.35) : 0.9 * (1 - (t - 0.35) / 0.65);
}
